import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteIcon from "@mui/icons-material/Delete";
import Folder from "@mui/icons-material/Folder";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { useTheme, alpha } from "@mui/material/styles";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { openAppConfirm } from "../../components/AppDialogs";
import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";
import MDTypography from "../../components/MDTypography";
import Footer from "../../examples/Footer";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {
  AddSlideCall,
  AddSubTitlesCall,
  AddTitlesCall,
  DeleteSubTitlesCall,
  DeleteTitlesCall,
  EditSubTitlesCall,
  EditTitlesCall,
  GetTitlesCall,
  SwapTitlesCall,
} from "../../redux/api/title.api";
import { getQuizLinkedIdsCall } from "../../redux/api/quizSystem.api";
import { store } from "../../redux/store";
import Chip from "@mui/material/Chip";
import ProfSelector from "components/ProfSelector";

function formatRelativeTime(dateStr, t) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return t ? t("pages.presentationTitles.justNow") : "Just now";
  if (diffMins < 60) return t ? t("pages.presentationTitles.minutesAgo_other", { count: diffMins }) : `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return t ? t("pages.presentationTitles.hoursAgo_other", { count: diffHours }) : `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return t ? t("pages.presentationTitles.daysAgo_other", { count: diffDays }) : `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function PresentationTitles() {
  const { t } = useTranslation();
  const theme = useTheme();
  const authId = useSelector((state) => state?.auth?.id);
  const role = useSelector((state) => state?.auth?.role);
  const [selectedProfId, setSelectedProfId] = useState("");
  // Presentations use a dedicated type so they don't mix with library titles.
  const presentationType = "presentation";
  const type = presentationType;

  const initialItems = useSelector((state) => state?.title?.titles);
  const redux = useSelector((state) => state);

  const [fileRefs, setFileRefs] = useState({}); // store refs by subtitle ID

  const [items, setItems] = useState(initialItems);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  const [lang, setLanguage] = useState("en");
  const [linkedTileIds, setLinkedTileIds] = useState([]);

  const openSuccessSB = () => setSuccessSB(true);
  const openErrorSB = () => setErrorSB(true);

  const navigate = useNavigate();
  const isViewingOtherProf = (role === "admin" || role === "university_admin") && !!selectedProfId;

  const fetchTitles = async () => {
    if (!type) return;
    await GetTitlesCall(lang, type);
  };

  useEffect(() => {
    fetchTitles();
  }, [type, lang]);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    const r = store.getState().auth?.role;
    if (r === "prof" || r === "admin" || r === "university_admin") {
      const params = (r === "admin" || r === "university_admin") && selectedProfId ? { prof_id: selectedProfId } : {};
      getQuizLinkedIdsCall(params)
        .then((res) => {
          const data = res?.data;
          if (data?.tile_ids) setLinkedTileIds(data.tile_ids);
        })
        .catch(() => setLinkedTileIds([]));
    }
  }, [selectedProfId]);

  const handleAddTitle = () => {
    AddTitlesCall({
      title: `New Title ${items?.length + 1}`,
      body: `Description ${items?.length + 1}`,
      lang,
      type,
    });
  };

  const handleAddSubtitle = (itemId) => {
    AddSubTitlesCall({
      title: `New Slide ${items?.length + 1}`,
      body: `Description ${items?.length + 1}`,
      title_id: itemId,
      lang,
      type,
    });
  };

  const handleEdit = (id, newValue, editType) => {
    const body = {
      id,
      lang,
      type,
      [editType === "MainTitle" || editType === "SubTitle" ? "title" : "body"]: newValue,
    };

    setItems((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (editType === "MainTitle" && item.id === id) {
          const locs = Array.isArray(item.titleLocalizations) ? [...item.titleLocalizations] : [];
          const titleLoc = locs.find((e) => e.field === "title");
          if (titleLoc) {
            return { ...item, titleLocalizations: locs.map((e) => (e.field === "title" ? { ...e, value: newValue } : e)) };
          }
          return { ...item, titleLocalizations: [...locs, { field: "title", value: newValue }] };
        }
        if (editType === "SubTitle" && item.subtitles) {
          return {
            ...item,
            subtitles: item.subtitles.map((sub) => {
              if (sub.id !== id) return sub;
              const locs = Array.isArray(sub.titleLocalizations) ? [...sub.titleLocalizations] : [];
              const newLocs = locs.some((e) => e.field === "title")
                ? locs.map((e) => (e.field === "title" ? { ...e, value: newValue } : e))
                : [...locs, { field: "title", value: newValue }];
              return { ...sub, titleLocalizations: newLocs };
            }),
          };
        }
        return item;
      });
    });

    if (editType === "MainTitle" || editType === "MainBody") {
      EditTitlesCall(body);
    } else {
      EditSubTitlesCall(body);
    }
  };

  const handleDeleteSubTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: t("pages.presentationTitles.deleteSubtitleConfirm"),
      message: t("pages.presentationTitles.deleteSubtitleConfirm"),
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = DeleteSubTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The subtitle deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
      }
    }
  };
  const handleDeleteTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: "Delete title?",
      message:
        "Are you sure you want to delete this title and all its subtitles? All your pins, texts and slides inside this subtitle will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage(t("pages.presentationTitles.titleDeletedSuccess"));
        setTimeout(() => {
          openSuccessSB();
        }, 200);
      }
    }
  };

  const handleAddMaterial = async (id) => {
    const res = await AddSlideCall({ title_id: id, lang, type });
    if (!res?.slide?.id) return;
    if (role === "admin") {
      navigate(`/presentationMakerPage/${id}/${lang}`);
    } else {
      navigate(`/presentationMakerPage/${id}`);
    }
  };

  const renderItem = (item, index) => {
    const filteredTitle = item?.titleLocalizations?.filter(
      (e) => e.field == "title"
    );

    const title = filteredTitle?.[0]?.value || t("pages.presentationTitles.clickToEditTitle");
    const isPublished = item?.is_published === true;
    const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
    const isDark = theme.palette.mode === "dark";
    const softBorder = isDark ? alpha(theme.palette.divider, 0.5) : alpha(theme.palette.primary.main, 0.08);

    return (
      <MDBox
        sx={{
          width: "100%",
          mb: 3,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          bgcolor: cardBg,
          borderRadius: 3,
          border: `1px solid ${softBorder}`,
          boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
          transition: "box-shadow 0.25s ease, border-color 0.2s ease",
          "&:hover": { boxShadow: isDark ? "0 0 0 1px " + alpha(theme.palette.primary.main, 0.2) : "0 4px 20px rgba(0,0,0,0.06)" },
        }}
      >
        {!isViewingOtherProf && (
          <MDBox display="flex" gap={0.5} sx={{ width: "100%", justifyContent: "flex-start" }}>
            {index !== 0 && (
              <IconButton
                size="small"
                onClick={() => {
                  const body = { current_id: item?.id, goal_id: items?.[index - 1]?.id, lang, type };
                  SwapTitlesCall(body);
                }}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                }}
              >
                <ArrowDropUpIcon fontSize="small" />
              </IconButton>
            )}
            {items?.length !== index + 1 && (
              <IconButton
                size="small"
                onClick={() => {
                  const body = { current_id: item?.id, goal_id: items?.[index + 1]?.id, lang, type };
                  SwapTitlesCall(body);
                }}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                }}
              >
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            )}
          </MDBox>
        )}

        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
          {isViewingOtherProf ? (
            <MDTypography variant="h5" fontWeight="700" sx={{ letterSpacing: "-0.02em" }}>{title}</MDTypography>
          ) : (
            <EditableTitle
              title={title}
              titleFont={"title"}
              onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
            />
          )}
          {linkedTileIds.includes(item.id) && (
            <Chip size="small" label={t("pages.presentationTitles.quiz")} color="info" onClick={() => navigate("/quiz/titles")} sx={{ cursor: "pointer", fontWeight: 600 }} />
          )}
        </MDBox>

        {!isViewingOtherProf && (
          <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ width: "100%", mt: 2.5, mb: 1 }}>
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
                borderRadius: 3,
                px: 2.5,
                py: 1.25,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }}
              onClick={() => handleAddSubtitle(item.id)}
            >
              {t("pages.presentationTitles.newPresentation")}
            </MDButton>
            <MDButton
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 3,
                px: 2,
                py: 1.25,
                borderWidth: 1.5,
                "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.08) },
              }}
              onClick={() => handleDeleteTitle(item.id)}
            >
              {t("common.delete")}
            </MDButton>
          </MDBox>
        )}

        {item?.subtitles?.length > 0 && (
          <MDBox sx={{ mt: 3, width: "100%" }}>
            {/* Header row: same flex as button row so columns align with New Presentation / Delete */}
            <MDBox
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 1.5,
                color: "text.secondary",
                typography: "caption",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                boxSizing: "border-box",
              }}
            >
              <MDBox sx={{ flex: "0 0 40px", display: "flex", justifyContent: "flex-start", alignItems: "center" }} />
              <MDBox sx={{ flex: "1 1 0%", minWidth: 0, display: "flex", justifyContent: "flex-start" }}>{t("common.name")}</MDBox>
              <MDBox sx={{ flex: "0 0 64px", display: "flex", justifyContent: "center" }}>{t("pages.presentationTitles.slides")}</MDBox>
              <MDBox sx={{ flex: "0 0 260px", display: "flex", justifyContent: "flex-end" }}>{t("pages.presentationTitles.created")}</MDBox>
              <MDBox sx={{ flex: "0 0 260px", display: "flex", justifyContent: "flex-end" }}>{t("pages.presentationTitles.lastEdited")}</MDBox>
              <MDBox sx={{ flex: "0 0 140px", display: "flex", justifyContent: "flex-end" }}>{t("pages.presentationTitles.status")}</MDBox>
              <MDBox sx={{ flex: "0 0 48px", display: "flex", justifyContent: "flex-end", alignItems: "center" }} />
            </MDBox>
            {item.subtitles.map((subtitle, idx) => {
              const filteredTitle = subtitle?.titleLocalizations?.filter(
                (e) => e.field == "title"
              );
              const title = filteredTitle?.[0]?.value || t("pages.presentationTitles.clickToEditSubtitle");
              const slideCount = Array.isArray(subtitle?.slides) ? subtitle.slides.length : 0;
              const createdAt = subtitle?.created_at ?? subtitle?.createdAt ?? null;
              const updatedAt = subtitle?.updated_at ?? subtitle?.updatedAt ?? createdAt;
              const isPublished = subtitle?.is_published === true;

              return (
                <MDBox
                  key={subtitle.id}
                  onClick={() => {
                    if (subtitle?.slides?.[0]?.id) {
                      if (role === "admin") {
                        navigate(`/presentationMakerPage/${subtitle.id}/${lang}`);
                      } else {
                        navigate(`/presentationMakerPage/${subtitle.id}`);
                      }
                    } else if (!isViewingOtherProf) {
                      handleAddMaterial(subtitle.id);
                    }
                  }}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    py: 1.5,
                    cursor: "pointer",
                    transition: "background-color 0.2s ease",
                    boxSizing: "border-box",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  }}
                >
                  <MDBox sx={{ flex: "0 0 40px", display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                    <Folder sx={{ color: "primary.main", fontSize: 22 }} />
                  </MDBox>
                  <MDBox
                    sx={{
                      flex: "1 1 0%",
                      minWidth: 0,
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <MDBox
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{ display: "inline-flex", maxWidth: "100%" }}
                    >
                      <EditableTitle
                        title={title}
                        size={14}
                        titleFont="title"
                        onEdit={(newTitle) => handleEdit(subtitle.id, newTitle, "SubTitle")}
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{ flex: "0 0 64px", display: "flex", justifyContent: "center" }}>
                    <MDTypography variant="body2" color="text.secondary">
                      {slideCount}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ flex: "0 0 260px", display: "flex", justifyContent: "flex-end" }}>
                    <MDTypography variant="body2" color="text.secondary" noWrap>
                      {formatRelativeTime(createdAt, t)}
                    </MDTypography>
                  </MDBox>
                  <MDBox sx={{ flex: "0 0 260px", display: "flex", justifyContent: "flex-end" }}>
                    <MDTypography variant="body2" color="text.secondary" noWrap>
                      {formatRelativeTime(updatedAt, t)}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      flex: "0 0 140px",
                      display: "flex",
                      justifyContent: "flex-end",
                      px: 1.25, // ~10px horizontal padding
                    }}
                  >
                    <Chip
                      size="small"
                      label={isPublished ? t("common.published") : t("common.unpublished")}
                      color={isPublished ? "success" : "default"}
                      sx={{ fontWeight: 600 }}
                    />
                  </MDBox>
                  <MDBox sx={{ flex: "0 0 48px", display: "flex", alignItems: "center", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSubTitle(subtitle.id);
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        )}
      </MDBox>
    );
  };

  const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
  const isDark = theme.palette.mode === "dark";
  const softBorder = isDark ? alpha(theme.palette.divider, 0.5) : alpha(theme.palette.primary.main, 0.12);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        sx={{
          width: "100%",
          maxWidth: "100%",
          px: 0,
          pt: 1.5,
          pb: 0,
          minHeight: "auto",
          bgcolor: "transparent",
        }}
      >
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: 3,
            bgcolor: cardBg,
            border: `1px solid ${softBorder}`,
            boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <ProfSelector value={selectedProfId} onChange={setSelectedProfId} />
          {!isViewingOtherProf && (
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
                borderRadius: 3,
                px: 2.5,
                py: 1.25,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }}
              onClick={() => handleAddTitle()}
            >
              {t("pages.presentationTitles.newCategory")}
            </MDButton>
          )}
        </MDBox>
        <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
          <Grid container spacing={3} sx={{ width: "100%", maxWidth: "100%" }}>
            {isEmpty(items) && (
              <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%" }}>
                <MDBox
                  sx={{
                    width: "100%",
                    py: { xs: 5, sm: 7 },
                    px: { xs: 2, sm: 4 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    bgcolor: cardBg,
                    borderRadius: 3,
                    border: `1px solid ${softBorder}`,
                    boxShadow: isDark ? "none" : "0 2px 12px rgba(0,0,0,0.04)",
                    maxWidth: 720,
                    mx: "auto",
                  }}
                >
                  <SlideshowOutlinedIcon
                    style={{
                      fontSize: 20,

                    }}
                    sx={{
                      fontSize: 56,
                      color: "primary.main",
                      opacity: 0.85,
                      mb: 2,
                    }}
                  />
                  <MDTypography variant="h5" fontWeight={700} sx={{ mb: 1.5, letterSpacing: "-0.02em" }}>
                    {isViewingOtherProf
                      ? t("pages.presentationTitles.emptyStateTitleViewingProf")
                      : t("pages.presentationTitles.emptyStateTitle")}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 520, lineHeight: 1.65, mb: isViewingOtherProf ? 0 : 3 }}
                  >
                    {isViewingOtherProf
                      ? t("pages.presentationTitles.emptyStateDescriptionViewingProf")
                      : t("pages.presentationTitles.emptyStateDescription")}
                  </MDTypography>
                  {!isViewingOtherProf && (
                    <MDButton
                      color="primary"
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        borderRadius: 3,
                        px: 2.5,
                        py: 1.25,
                        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                      }}
                      onClick={() => handleAddTitle()}
                    >
                      {t("pages.presentationTitles.emptyStateCta")}
                    </MDButton>
                  )}
                </MDBox>
              </Grid>
            )}
            {!isEmpty(items) &&
              items.map((item, index) => (
                <Grid item xs={12} key={item.id} sx={{ width: "100%", maxWidth: "100%" }}>
                  {renderItem(item, index)}
                </Grid>
              ))}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

const EditableTitle = ({ size = null, title, onEdit, titleFont }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(title);
  let fontSize = "1rem";
  let fontWeight = 400;

  if (titleFont == "title") {
    fontSize = size ? `${size}px` : "1.25rem";
    fontWeight = 700;
  }
  const handleBlur = () => {
    setIsEditing(false);
    onEdit(currentTitle);
  };

  return isEditing ? (
    <MDBox display="flex" alignItems="center" gap={1} sx={{ width: "100%", justifyContent: "center" }}>
      <Box
        component="input"
        type="text"
        value={currentTitle}
        onChange={(e) => setCurrentTitle(e.target.value)}
        onBlur={handleBlur}
        autoFocus
        sx={{
          fontSize,
          fontWeight,
          textAlign: "center",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background?.paper ?? theme.palette.background?.default,
          color: "text.primary",
          outline: "none",
          maxWidth: "80%",
          "&:focus": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        }}
      />
      <MDButton
        size="small"
        color="success"
        variant="contained"
        sx={{
          minWidth: 40,
          borderRadius: 2,
          boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.35)}`,
          "&:hover": { boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.45)}` },
        }}
        onClick={() => { handleBlur(); onEdit(currentTitle); }}
      >
        <Icon fontSize="small">check</Icon>
      </MDButton>
    </MDBox>
  ) : (
    <MDTypography
      variant="h6"
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: "pointer",
        textAlign: "center",
        fontSize,
        fontWeight,
        "&:hover": { color: "primary.main" },
      }}
    >
      {title}
    </MDTypography>
  );
};

export default PresentationTitles;
